import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ContributionFormModal from './components/ContributionFormModal';
import SubmissionConfirmation from './components/SubmissionConfirmation';
import PromoVideoSection from './components/PromoVideoSection';

const FONT_DISPLAY = "-apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif";
const FONT_BODY = "-apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif";

const AboutTrustCenter = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [supportSent, setSupportSent] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittedName, setSubmittedName] = useState('');

  const pillars = [
    {
      icon: 'Layers',
      label: 'Immersion 3D',
      text: 'Chaque propriété devient un espace à vivre avant même de la visiter. Nos modèles 3D interactifs redéfinissent la découverte immobilière.',
      number: '01'
    },
    {
      icon: 'Shield',
      label: 'Confiance Absolue',
      text: 'Titres vérifiés, notaires certifiés, transactions sécurisées. La transparence n\'est pas une option — c\'est notre fondation.',
      number: '02'
    },
    {
      icon: 'MapPin',
      label: 'Ancrage Local',
      text: 'Abidjan dans toute sa complexité : 10 communes, des milliers d\'opportunités, une seule plateforme qui les connaît toutes.',
      number: '03'
    }
  ];

  const stats = [
    { value: '1 200+', label: 'Biens vendus' },
    { value: '2 500+', label: 'Clients satisfaits' },
    { value: '10', label: 'Communes couvertes' },
    { value: '98%', label: 'Taux de satisfaction' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSent(true);
  };

  return (
    <>
      <Helmet>
        <title>À Propos — Kapital States | L'Immobilier Immersif en 3D à Abidjan</title>
        <meta name="description" content="Kapital States réinvente l'immobilier à Abidjan grâce à la visite 3D immersive. Découvrez notre vision, nos engagements et notre équipe." />
      </Helmet>
      
      {/* 
        Container Principal : Noir absolu 
      */}
      <div className="min-h-screen bg-[#050505] text-white border-none outline-none overflow-x-hidden" style={{ fontFamily: FONT_BODY }}>
        
        {/* Glow global d'ambiance */}
        <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
          <div className="absolute top-0 right-0 w-[800px] h-[600px] bg-brand-gold/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-gold/5 rounded-full blur-[150px] translate-y-1/3 -translate-x-1/4" />
        </div>

        {/* Le header doit hériter du fond transparent ou noir */}
        <Header className="bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50" />

        <main className="relative z-10">

          {/* ── HERO ─────────────────────────────────────────────── */}
          <section className="relative min-h-[90vh] flex items-center pt-24 pb-16">
            
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
              {/* Eyebrow */}
              <div className="flex items-center gap-4 mb-12">
                <div className="h-px w-10 bg-brand-gold" />
                <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">
                  Kapital States · Abidjan
                </span>
              </div>

              {/* Hero content: grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                
                {/* Left: Headline + CTA */}
                <div>
                  <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white mb-8 leading-tight tracking-tighter">
                    L'immobilier<br />
                    <span className="text-brand-gold">en trois</span><br />
                    <span className="text-white/40 font-light">dimensions.</span>
                  </h1>

                  <p className="text-white/60 text-lg md:text-xl leading-relaxed max-w-lg mb-12 font-light">
                    Nous ne vendons pas des mètres carrés. Nous créons des expériences — immersives, transparentes, ancrées dans la réalité d'Abidjan.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <Link to="/property-search-listings" className="w-full sm:w-auto">
                      <button className="w-full flex items-center justify-center gap-3 bg-brand-gold text-black px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-brand-gold/20 border-none outline-none">
                        Explorer les biens
                        <Icon name="ArrowRight" size={18} strokeWidth={2.5} />
                      </button>
                    </Link>
                    <Link to="/consultation-booking-hub" className="w-full sm:w-auto">
                      <button className="w-full bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-bold text-base transition-colors border-none outline-none">
                        Prendre rendez-vous
                      </button>
                    </Link>
                  </div>

                  {/* Stats row */}
                  <div className="mt-20 grid grid-cols-2 gap-8 md:gap-12">
                    {stats?.map((s, i) => (
                      <div key={i} className="relative">
                        {i > 0 && <div className="hidden md:block absolute -left-6 top-2 bottom-2 w-px bg-white/10" />}
                        <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-2 tracking-tight">
                          {s?.value}
                        </div>
                        <div className="text-white/40 text-xs font-bold uppercase tracking-widest">
                          {s?.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Promo Video Section */}
                <div className="relative z-20">
                  {/* Assuming PromoVideoSection has no white backgrounds or hard borders. If it does, adapt it in its file. */}
                  <PromoVideoSection />
                </div>
              </div>
            </div>
          </section>

          {/* ── VISION ───────────────────────────────────────────── */}
          <section className="py-32 relative">
            <div className="absolute inset-0 bg-[#0A0A0A]" /> {/* Slightly lighter black for depth */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-[1fr_2fr] gap-16 lg:gap-24 items-start">
                <div>
                  <div className="h-1 w-12 bg-brand-gold mb-8" />
                  <h2 className="text-4xl md:text-5xl font-heading font-bold text-white leading-tight tracking-tight">
                    Pourquoi<br />nous existons
                  </h2>
                </div>
                <div className="space-y-8 text-lg md:text-xl text-white/60 font-light leading-relaxed">
                  <p>
                    Le marché immobilier abidjanais méritait mieux qu'une simple liste de photos. Nous avons construit la première plateforme qui vous permet de <strong className="text-white font-medium">marcher dans un appartement</strong> depuis votre téléphone — avant même de contacter un agent.
                  </p>
                  <p className="text-white/40">
                    Notre ambition : devenir le leader de l'immobilier immersif en Afrique de l'Ouest, en combinant technologie 3D de pointe, expertise locale irremplaçable et rigueur juridique absolue.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── PILLARS ──────────────────────────────────────────── */}
          <section className="py-32 relative">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {pillars?.map((p, i) => (
                  <div 
                    key={i}
                    className={`rounded-3xl p-10 md:p-12 relative overflow-hidden group transition-transform duration-500 hover:-translate-y-2 border-none outline-none ${
                      i === 0 ? 'bg-gradient-to-br from-brand-gold/10 to-transparent' : 'bg-[#111111]'
                    }`}
                  >
                    <div className={`absolute top-8 right-8 text-xs font-bold tracking-widest ${i === 0 ? 'text-brand-gold/50' : 'text-white/20'}`}>
                      {p?.number}
                    </div>

                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 shadow-inner ${
                      i === 0 ? 'bg-brand-gold/20' : 'bg-white/5'
                    }`}>
                      <Icon name={p?.icon} size={24} className={i === 0 ? 'text-brand-gold' : 'text-white/50'} />
                    </div>

                    <h3 className="text-2xl font-heading font-bold text-white mb-4 tracking-tight">
                      {p?.label}
                    </h3>

                    <p className="text-white/50 font-light leading-relaxed">
                      {p?.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── MANIFESTO BAND ───────────────────────────────────── */}
          <section className="py-32 bg-brand-gold text-black text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
            <div className="max-w-4xl mx-auto px-6 relative z-10">
              <div className="text-8xl leading-none font-serif opacity-20 mb-4 -mt-10">"</div>
              <p className="text-3xl md:text-5xl font-heading font-bold leading-tight tracking-tight mb-12">
                L'avenir de l'immobilier ne se lit pas — il se vit. Chaque visite 3D est une promesse : voir vrai, décider libre.
              </p>
              <div className="flex items-center justify-center gap-4 text-xs font-bold uppercase tracking-widest opacity-80">
                <div className="h-px w-8 bg-black" />
                <span>Équipe Fondatrice, Kapital States</span>
                <div className="h-px w-8 bg-black" />
              </div>
            </div>
          </section>

          {/* ── CONTACT ──────────────────────────────────────────── */}
          <section className="py-32 relative">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-20 items-center">
                
                {/* Left */}
                <div>
                  <div className="h-1 w-12 bg-brand-gold mb-8" />
                  <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
                    Parlons de<br />votre projet
                  </h2>
                  <p className="text-white/50 text-lg font-light leading-relaxed mb-12 max-w-md">
                    Que vous soyez acheteur, investisseur ou simplement curieux — notre équipe est là pour vous guider.
                  </p>

                  <div className="space-y-4">
                    {[
                      { icon: 'Phone', label: '+225 21 50 00 53 18', sub: 'Lun–Ven 8h–18h, Sam 9h–15h' },
                      { icon: 'Mail', label: 'contact@kapitalstates.com', sub: 'Réponse sous 2h' },
                      { icon: 'MessageCircle', label: '+225 01 41 07 02 71', sub: 'WhatsApp 24/7' }
                    ]?.map((c, i) => (
                      <div 
                        key={i} 
                        className="flex items-center gap-6 p-6 rounded-2xl bg-[#111111] hover:bg-[#1A1A1A] transition-colors border-none group cursor-pointer"
                      >
                        <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                          <Icon name={c?.icon} size={20} className="text-brand-gold" />
                        </div>
                        <div>
                          <div className="font-bold text-white text-lg tracking-wide mb-1">{c?.label}</div>
                          <div className="text-white/40 text-xs font-light tracking-wide">{c?.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right — Form */}
                <div className="bg-[#111111] rounded-3xl p-10 md:p-12 shadow-2xl border-none outline-none relative overflow-hidden">
                  {/* Subtle top glow */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  
                  {sent ? (
                    <div className="text-center py-16 animate-fade-in">
                      <div className="w-20 h-20 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <Icon name="CheckCircle" size={32} className="text-brand-gold" />
                      </div>
                      <h3 className="font-heading font-bold text-2xl text-white mb-2 tracking-tight">Message envoyé</h3>
                      <p className="text-white/50 font-light">Nous vous répondrons dans les 2 heures.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                      {[
                        { name: 'name', label: 'Nom complet', type: 'text', placeholder: 'Votre nom' },
                        { name: 'email', label: 'Email', type: 'email', placeholder: 'votre@email.com' }
                      ]?.map((field) => (
                        <div key={field?.name}>
                          <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                            {field?.label}
                          </label>
                          <input
                            type={field?.type}
                            name={field?.name}
                            value={formData?.[field?.name]}
                            onChange={handleChange}
                            required
                            placeholder={field?.placeholder}
                            className="w-full px-5 py-4 bg-white/5 border-none rounded-2xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none transition-all shadow-inner"
                          />
                        </div>
                      ))}
                      <div>
                        <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                          Message
                        </label>
                        <textarea
                          name="message"
                          value={formData?.message}
                          onChange={handleChange}
                          required
                          rows={4}
                          placeholder="Décrivez votre projet..."
                          className="w-full px-5 py-4 bg-white/5 border-none rounded-2xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none resize-none transition-all shadow-inner"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-4 mt-2 bg-brand-gold text-black font-bold rounded-2xl hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-brand-gold/20 border-none"
                      >
                        Envoyer
                        <Icon name="Send" size={18} strokeWidth={2.5} />
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

        </main>

        {/* ── FOOTER ───────────────────────────────────────────── */}
        <footer className="bg-[#050505] py-12 border-t border-white/5 relative z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-black font-heading font-bold text-sm">KS</span>
                </div>
                <span className="font-heading font-bold text-white tracking-tight text-lg">
                  Kapital States
                </span>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-8">
                {[
                  { to: '/homepage', label: 'Accueil' },
                  { to: '/property-search-listings', label: 'Propriétés' },
                  { to: '/commune-explorer', label: 'Communes' },
                  { to: '/legal-services-center', label: 'Services Juridiques' }
                ]?.map((link) => (
                  <Link 
                    key={link?.to} 
                    to={link?.to} 
                    className="text-white/60 hover:text-brand-gold text-sm font-medium transition-colors"
                  >
                    {link?.label}
                  </Link>
                ))}
              </div>

              <p className="text-white/30 text-xs font-light tracking-wide">
                © {new Date()?.getFullYear()} Kapital States
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Modale */}
      {showModal && selectedTier !== null && (
        <ContributionFormModal
          selectedTier={selectedTier}
          onClose={() => setShowModal(false)}
          onSuccess={(name) => {
            setShowModal(false);
            setSubmittedName(name || '');
            setSupportSent(true);
          }} 
        />
      )}
    </>
  );
};

export default AboutTrustCenter;